import { Component, type ErrorInfo, type ReactNode } from 'react';

import { Button, Icon } from '@/components/ui';

import styles from './ErrorBoundary.module.css';

interface ErrorBoundaryProps {
  children: ReactNode;
  /** Replaces the default fallback UI. */
  fallback?: ReactNode;
}

interface ErrorBoundaryState {
  error: Error | null;
}

/**
 * Catches render-time errors below it so one broken page cannot blank the
 * whole site. Error boundaries must be class components.
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  override state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // Swap for your monitoring client (Sentry, Rollbar, …).
    console.error('Unhandled UI error:', error, info.componentStack);
  }

  private readonly handleReset = () => {
    this.setState({ error: null });
  };

  override render(): ReactNode {
    const { error } = this.state;
    const { children, fallback } = this.props;

    if (!error) return children;
    if (fallback) return fallback;

    return (
      <div className={styles.wrapper} role="alert">
        <span className={styles.icon}>
          <Icon name="close" size={26} />
        </span>
        <h1>Something went wrong</h1>
        <p className={styles.detail}>
          The page hit an unexpected error. Trying again usually helps; if it does not, the details
          below are useful to us.
        </p>
        <pre className={styles.code}>{error.message}</pre>
        <Button onClick={this.handleReset}>Try again</Button>
      </div>
    );
  }
}
